

const PrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-12 md:py-20 text-gray-700 dark:text-gray-300 antialiased selection:bg-orange-500/10 selection:text-orange-500">
      <div className="border-b border-gray-200 dark:border-gray-800 pb-8 mb-10">
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Last updated: <time dateTime="2026-04-20" className="font-medium text-gray-500 dark:text-gray-400">20 April 2026</time>
        </p>
      </div>

      <div className="space-y-10 text-base md:text-lg leading-relaxed">
        
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            1. Introduction
          </h2>
          <p>
            This Privacy Policy explains how <strong className="text-gray-900 dark:text-white font-semibold">GetEat</strong> collects,
            uses, and protects your personal data in accordance with the General Data Protection Regulation (GDPR).
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            2. Data We Collect
          </h2>
          <p className="mb-3">We collect the following personal data:</p>
          <ul className="list-disc pl-5 space-y-1 mb-4 marker:text-orange-500">
            <li>First name</li>
            <li>Last name</li>
            <li>Email address</li>
          </ul>
          
          <p className="mb-3 text-gray-600 dark:text-gray-400">You may also provide restaurant data:</p>
          <ul className="list-disc pl-5 space-y-1 marker:text-orange-500">
            <li>Address</li>
            <li>Phone number</li>
            <li>Coordinates</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            3. Legal Basis for Processing (GDPR)
          </h2>
          <p className="mb-3">We process your data based on:</p>
          <ul className="list-disc pl-5 space-y-1 marker:text-orange-500">
            <li><strong className="text-gray-900 dark:text-white font-medium">Contract:</strong> to provide and maintain the service.</li>
            <li><strong className="text-gray-900 dark:text-white font-medium">Legitimate interest:</strong> for service improvement, analytics, and security.</li>
            <li><strong className="text-gray-900 dark:text-white font-medium">Consent:</strong> for cookies and marketing, where applicable.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            4. Use of Data
          </h2>
          <p className="mb-3">Your data is used to:</p>
          <ul className="list-disc pl-5 space-y-1 marker:text-orange-500">
            <li>Create and manage your user account</li>
            <li>Communicate with you regarding updates or support</li>
          </ul>
        </section>

        <section className="bg-amber-50 dark:bg-amber-950/20 border-l-4 border-amber-500 p-4 rounded-r-lg my-6">
          <h3 className="text-amber-800 dark:text-amber-400 font-bold text-sm uppercase tracking-wide mb-1">
            5. Public Data Notice
          </h3>
          <p className="text-amber-900 dark:text-amber-300 text-sm md:text-base">
            Restaurant data (<span className="italic">address, phone number, coordinates</span>) is considered public information and will be visible to all users on the platform.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            6. Data Sharing
          </h2>
          <p className="mb-2">
            We <span className="underline decoration-red-500 decoration-2 font-medium text-gray-900 dark:text-white">do not sell, rent, or share</span> your personal data with third parties.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Data may only be disclosed if strictly required by applicable law or judicial authorities.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            7. Data Retention
          </h2>
          <p>
            We retain your data only as long as necessary to provide the requested services or to comply with our legal and regulatory obligations.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            8. Your Rights (GDPR)
          </h2>
          <p className="mb-3">As a GDPR-protected user, you have the right to:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm md:text-base">
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 rounded-md">🟢 Access your data</div>
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 rounded-md">🟢 Correct inaccurate data</div>
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 rounded-md">🟢 Request deletion ("Right to be forgotten")</div>
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 rounded-md">🟢 Restrict data processing</div>
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 rounded-md">🟢 Request data portability</div>
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 rounded-md">🟢 Withdraw consent at any time</div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            9. Cookies
          </h2>
          <p className="mb-3">We use cookies in the following categories:</p>
          <ul className="list-disc pl-5 space-y-2 marker:text-orange-500">
            <li>
              <strong className="text-gray-900 dark:text-white">Statistical cookies</strong> — used for internal analytics to understand how users interact with the app.
            </li>
            <li>
              <strong className="text-gray-900 dark:text-white">Marketing cookies</strong> — used for tailoring advertising relevant to your preferences.
            </li>
          </ul>
          <p className="mt-3 text-sm text-gray-500">
            Cookies are only deployed based on your active consent where required by law.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            10. Data Security
          </h2>
          <p>
            We implement industry-standard technical and organizational security measures to protect your data from unauthorized access, alteration, or disclosure.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            11. Changes to this Policy
          </h2>
          <p>
            We reserve the right to update this Privacy Policy. Any changes will be immediately effective upon posting the updated version on this page.
          </p>
        </section>

        <section className="mt-12 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
              12. Contact Us
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              If you have any questions about this Privacy Policy, please contact our support.
            </p>
          </div>
          <a 
            href="mailto:support@geteat.net" 
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-lg text-sm font-medium transition-colors border border-transparent"
          >
            support@geteat.net
          </a>
        </section>

      </div>
    </div>
  );
};

export default PrivacyPolicy;
